import { useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { Switch } from "./ui/switch"
import { Badge } from "./ui/badge"
import { toast } from "sonner"
import { obtenerPermisos, actualizarPermiso } from "../services/pruebasService"
import { ACCIONES_PERMISOS, GRUPOS_PERMISOS, ROLES_EDITABLES, ROL_LABELS, type RolEditable } from "../lib/permisos"

type Matriz = Record<string, Record<string, boolean>>

export function PermisosView() {
  const [matriz, setMatriz] = useState<Matriz>({})
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState<string | null>(null)

  useEffect(() => {
    obtenerPermisos()
      .then(data => {
        const m: Matriz = {}
        data.forEach(p => {
          if (!m[p.rol_id]) m[p.rol_id] = {}
          m[p.rol_id][p.accion] = p.permitido
        })
        setMatriz(m)
      })
      .catch(err => toast.error("No se pudieron cargar los permisos: " + err.message))
      .finally(() => setLoading(false))
  }, [])

  const grupos = useMemo(
    () => GRUPOS_PERMISOS.map(grupo => ({
      grupo,
      acciones: ACCIONES_PERMISOS.filter(a => a.grupo === grupo),
    })),
    []
  )

  const handleToggle = async (rol: RolEditable, accion: string, valorActual: boolean) => {
    const nuevoValor = !valorActual
    const cellKey = `${rol}__${accion}`
    setMatriz(prev => ({ ...prev, [rol]: { ...prev[rol], [accion]: nuevoValor } }))
    setGuardando(cellKey)
    try {
      await actualizarPermiso(rol, accion, nuevoValor)
    } catch (err: any) {
      // revertir en caso de error
      setMatriz(prev => ({ ...prev, [rol]: { ...prev[rol], [accion]: valorActual } }))
      toast.error("No se pudo guardar el cambio: " + (err.message || ''))
    } finally {
      setGuardando(null)
    }
  }

  if (loading) {
    return <div className="text-muted-foreground animate-pulse">Cargando permisos...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1>Permisos</h1>
        <p className="text-muted-foreground">
          Define qué puede hacer cada rol. Los cambios se guardan automáticamente.
        </p>
      </div>

      {grupos.map(({ grupo, acciones }) => (
        <Card key={grupo}>
          <CardHeader>
            <CardTitle className="text-base">{grupo}</CardTitle>
            <CardDescription>
              {acciones.length} {acciones.length === 1 ? 'acción' : 'acciones'} en esta categoría
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Acción</TableHead>
                  {ROLES_EDITABLES.map(rol => (
                    <TableHead key={rol} className="text-center">{ROL_LABELS[rol]}</TableHead>
                  ))}
                  <TableHead className="text-center">Administrador</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {acciones.map(accion => (
                  <TableRow key={accion.key}>
                    <TableCell>
                      <div className="font-medium">{accion.label}</div>
                      <div className="text-xs text-muted-foreground">{accion.descripcion}</div>
                    </TableCell>
                    {ROLES_EDITABLES.map(rol => {
                      const permitido = !!matriz[rol]?.[accion.key]
                      const cellKey = `${rol}__${accion.key}`
                      return (
                        <TableCell key={rol} className="text-center">
                          <Switch
                            checked={permitido}
                            disabled={guardando === cellKey}
                            onCheckedChange={() => handleToggle(rol, accion.key, permitido)}
                          />
                        </TableCell>
                      )
                    })}
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="text-xs">Siempre permitido</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
